<template>
  <GoogleMaps class="listing-map">
    <div class="listing-map__wrapper" ref="mapWrapper"/>
    <ResetMapButton @click="resetMapPosition"/>
    <ZoomButton class="map-zoom--in" @click="zoomIn" />
    <ZoomButton class="map-zoom--out" @click="zoomOut" />
    <FullScreenButton @click="fullScreen" />
    <FullScreenButton class="map-fullscreen--exit" @click="exitFullScreen" />
  </GoogleMaps>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import FullScreenButton from '@/components/googleMap/buttons/FullScreen'
import ResetMapButton from '@/components/googleMap/buttons/ResetMap'
import ZoomButton from '@/components/googleMap/buttons/Zoom'
import ListingMapMixin from '@/components/googleMap/ListingMapMixin'
import ListingMapOffer from '@/components/googleMap/ListingMapOffer'
import GoogleMaps from '@/components/GoogleMaps'
import GoogleMapsMixin from '@/components/GoogleMapsMixin'

@Component({
  mixins: [
    GoogleMapsMixin,
    ListingMapMixin,
  ],
  components: {
    GoogleMaps,
    ResetMapButton,
    ZoomButton,
    FullScreenButton,
  },
  props: {
    offers: {
      type: Array,
      required: false,
    },
    priceType: {
      type: String,
      required: false,
    },
    mapConfig: {
      type: Object,
      required: true,
    },
  },
  watch: {
    priceType (val) {
      this.priceType = val
      this.updateOfferMarkerLabel()
    },
  },
})
export default class ListingMap extends Vue {
  mounted () {
    return Promise.resolve()
      .then(() => this.setMapData(this.mapConfig))
      .then(() => this.setMapRootElements())
      .then(() => this.init())
  }

  setMapRootElements () {
    this.mapWrapper = this.$refs.mapWrapper
  }

  resetMapPosition () {
    this.setDefaultMapPosition(this.googleMap, this.mapData)
  }

  zoomIn () {
    this.zoomMapIn(this.googleMap)
  }

  zoomOut () {
    this.zoomMapOut(this.googleMap)
  }

  fullScreen () {
    this.fullScreenMap()
  }

  exitFullScreen () {
    this.exitFullScreenMap()
  }

  mapSettings () {
    return {
      zoom: parseInt(this.mapData.zoom),
      center: this.getCenterCoords(this.mapData),
      styles: this.loadCustomMapStyles(this.mapData.styles),
      disableDefaultUI: true,
    }
  }

  getMarkerConfig () {
    return {
      url: '/assets/images/Maps/Listingmap/MapPriceTag.svg',
      labelOrigin: new google.maps.Point(31, 13),
      scaledSize: {
        width: 62,
        height: 32,
      },
    }
  }

  getMarkerHoverConfig () {
    return {
      url: '/assets/images/Maps/Listingmap/MapPriceTagHover.svg',
      labelOrigin: new google.maps.Point(31, 13),
      scaledSize: {
        width: 62,
        height: 32,
      },
    }
  }

  setOfferPriceText (offer) {
    return this.priceType === 'person' ? offer.Merlin_AdultPrice : offer.Merlin_FullPriceParsed
  }

  setOfferMarkerLabel (offer) {
    return {
      text: `${this.setOfferPriceText(offer)} zł`,
      fontFamily: 'Gotham',
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
    }
  }

  /**
   * This one is needed because we have two different price groups - per person & per group.
   * Because we dont want to re-render every marker every time we change price option,
   * we can simply set only new labels on already defined markers.
   */
  updateOfferMarkerLabel () {
    this.offerMarkers.forEach((marker, i) => {
      marker.setLabel({
        text: `${this.setOfferPriceText(this.offers[i])} zł`,
        fontFamily: 'Gotham',
        color: 'white',
        fontSize: '14',
        fontWeight: 'bold',
      })
    })
  }

  createPlaceholderForOffer (e) {
    const div = document.createElement('div')
    e.wa.target.parentElement.insertAdjacentElement('beforeend', div)
    return div
  }

  initMapOffer (offer, marker, e) {
    if (document.querySelector('.listing-map-offer')) document.querySelector('.listing-map-offer').remove()
    if (this.offerListWrapperExist()) this.offerListWrapperExist().classList.remove('listing-map__wrapper--offer-wrapper')

    const priceType = this.priceType
    const googleMap = this.googleMap

    marker.setIcon(this.getMarkerConfig())

    return new ListingMapOffer({
      el: this.createPlaceholderForOffer(e),
      propsData: { offer, priceType, googleMap },
    })
  }

  setOffersOnMap () {
    this.offers.forEach(offer => {
      const hotelLat = parseFloat(offer.Hotel_GPS_Latitude.replace(/,/g, '.'))
      const hotelLng = parseFloat(offer.Hotel_GPS_Longitude.replace(/,/g, '.'))

      const position = this.setNewMarkerPosition(hotelLat, hotelLng)
      const marker = this.getMarker(position, this.googleMap, this.getMarkerConfig(), this.setOfferMarkerLabel(offer))
      this.offerMarkers.push(marker)

      google.maps.event.addListener(marker, 'mouseover', () => marker.setIcon(this.getMarkerHoverConfig()))
      google.maps.event.addListener(marker, 'mouseout', () => marker.setIcon(this.getMarkerConfig()))
      google.maps.event.addListener(marker, 'click', this.initMapOffer.bind(null, offer, marker))
    })
  }

  init () {
    this.googleMap = this.setNewMap(this.mapWrapper, this.mapSettings())  
    this.setOffersOnMap()
    this.initMarkerClusterer(this.offerMarkers)
  }
}
</script>

<style lang="scss">
.listing-map__wrapper {
  height: 400px;

  @include media(tablet) {
    height: 500px;
  }
  @include media(desktop) {
    height: 600px;
  }

  &--offer {
    position: absolute;
    z-index: 200;
    color: red;
    font-size: 40px;
  }

  // need to override google styles
  &--offer-wrapper {
    opacity: 1 !important;
    overflow: initial !important;
    z-index: 1 !important;
  }
}

.fullscreen {
  position: initial;

  .listing-map__wrapper {
    position: fixed !important;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 9999;
  }

  .map-fullscreen--exit {
    display: block;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/googleMap/ListingMap.vue